import React, { useState, useRef, useEffect } from 'react';
import '../styles/CalendarRangePicker.css';

const WEEKDAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
const MONTHS = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
];
const MONTHS_SHORT = [
  'Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun',
  'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'
];
const YEAR_PAGE = 12;

function parseDate(str) {
  if (!str) return null;
  const [y, m, d] = str.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function formatDate(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function formatShort(str) {
  if (!str) return null;
  const d = parseDate(str);
  return `${d.getDate()}. ${MONTHS[d.getMonth()].slice(0, 3)}`;
}

function isSameDay(a, b) {
  return a && b &&
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
}

const CalendarRangePicker = ({ startDate, endDate, onRangeChange }) => {
  const todayRaw = new Date();
  todayRaw.setHours(0, 0, 0, 0);

  const initYear = startDate ? parseDate(startDate).getFullYear() : todayRaw.getFullYear();
  const initMonth = startDate ? parseDate(startDate).getMonth() : todayRaw.getMonth();

  const [isOpen, setIsOpen] = useState(false);
  // 'start' = user is picking the start date, 'end' = picking end date
  const [selMode, setSelMode] = useState('start');
  // 'days' | 'months' | 'years'
  const [viewMode, setViewMode] = useState('days');
  const [viewYear, setViewYear] = useState(initYear);
  const [viewMonth, setViewMonth] = useState(initMonth);
  const [yearPageStart, setYearPageStart] = useState(initYear - 4);
  const [hoverDate, setHoverDate] = useState(null);

  const containerRef = useRef(null);
  const start = parseDate(startDate);
  const end = parseDate(endDate);

  // Show hover preview only when in end-mode and hovering after start
  const effectiveEnd =
    selMode === 'end' && hoverDate && start && hoverDate > start
      ? hoverDate
      : end;

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handle = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setHoverDate(null);
      }
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [isOpen]);

  const handleOpen = () => {
    if (!isOpen) {
      setViewMode('days');
      setSelMode('start');
    }
    setIsOpen(o => !o);
    setHoverDate(null);
  };

  const handleDayClick = (date) => {
    if (selMode === 'start') {
      onRangeChange({ startDate: formatDate(date), endDate: '' });
      setSelMode('end');
    } else {
      if (!start || date > start) {
        onRangeChange({ startDate, endDate: formatDate(date) });
        setHoverDate(null);
        setIsOpen(false);
      } else {
        // Clicked before or on start → become new start, stay in end mode
        onRangeChange({ startDate: formatDate(date), endDate: '' });
      }
    }
  };

  const prevNav = () => {
    if (viewMode === 'years') {
      setYearPageStart(s => s - YEAR_PAGE);
    } else {
      if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
      else setViewMonth(m => m - 1);
    }
  };

  const nextNav = () => {
    if (viewMode === 'years') {
      setYearPageStart(s => s + YEAR_PAGE);
    } else {
      if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
      else setViewMonth(m => m + 1);
    }
  };

  const openMonthPicker = () => setViewMode('months');
  const openYearPicker = () => {
    setYearPageStart(viewYear - 4);
    setViewMode('years');
  };

  const selectMonth = (m) => { setViewMonth(m); setViewMode('days'); };
  const selectYear = (y) => { setViewYear(y); setViewMode('days'); };

  const buildCells = () => {
    const firstDay = new Date(viewYear, viewMonth, 1).getDay();
    const offset = firstDay === 0 ? 6 : firstDay - 1;
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
    const cells = new Array(offset).fill(null);
    for (let d = 1; d <= daysInMonth; d++) {
      cells.push(new Date(viewYear, viewMonth, d));
    }
    return cells;
  };

  const isInRange = (date) => {
    if (!date || !start || !effectiveEnd) return false;
    const lo = start <= effectiveEnd ? start : effectiveEnd;
    const hi = start <= effectiveEnd ? effectiveEnd : start;
    return date > lo && date < hi;
  };

  const years = Array.from({ length: YEAR_PAGE }, (_, i) => yearPageStart + i);
  const cells = buildCells();

  const displayText = !startDate && !endDate
    ? null
    : startDate && !endDate
      ? `${formatShort(startDate)} – ?`
      : `${formatShort(startDate)} bis ${formatShort(endDate)}`;

  const navLabel = viewMode === 'years'
    ? `${years[0]} – ${years[years.length - 1]}`
    : null;

  return (
    <div className="crp" ref={containerRef}>
      {/* ── Trigger ── */}
      <button
        type="button"
        className={`crp__trigger${isOpen ? ' crp__trigger--open' : ''}${displayText ? ' crp__trigger--active' : ''}`}
        onClick={handleOpen}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
        <span className="crp__trigger-text">{displayText || 'Datum wählen'}</span>
        {displayText && (
          <span className="crp__clear" role="button" tabIndex={-1}
            onClick={(e) => { e.stopPropagation(); onRangeChange({ startDate: '', endDate: '' }); }}>
            ✕
          </span>
        )}
      </button>

      {/* ── Dropdown ── */}
      {isOpen && (
        <div className="crp__dropdown">

          {/* Mobile header */}
          <div className="crp__mobile-header">
            <span>Zeitraum wählen</span>
            <button type="button" className="crp__mobile-close" onClick={() => setIsOpen(false)}>✕</button>
          </div>

          {/* Month / Year navigation */}
          <div className="crp__nav">
            <button type="button" className="crp__nav-btn" onClick={prevNav}>
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>

            <span className="crp__nav-label">
              {viewMode === 'years' ? (
                <span className="crp__nav-year-range">{navLabel}</span>
              ) : (
                <>
                  <button type="button" className="crp__nav-month" onClick={openMonthPicker}>
                    {MONTHS[viewMonth]}
                  </button>
                  <button type="button" className="crp__nav-year" onClick={openYearPicker}>
                    {viewYear}
                  </button>
                </>
              )}
            </span>

            <button type="button" className="crp__nav-btn" onClick={nextNav}>
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </div>

          {/* ── Days view ── */}
          {viewMode === 'days' && (
            <>
              <div className="crp__weekdays">
                {WEEKDAYS.map(d => <div key={d} className="crp__weekday">{d}</div>)}
              </div>
              <div className="crp__days">
                {cells.map((date, i) => {
                  if (!date) return <div key={`e${i}`} className="crp__day crp__day--empty" />;

                  const isStart = isSameDay(date, start);
                  const isEnd = isSameDay(date, end);
                  const isEffectiveEnd = isSameDay(date, effectiveEnd);
                  const inRange = isInRange(date);
                  const isToday = isSameDay(date, todayRaw);
                  const stripRight = isStart && effectiveEnd && start <= effectiveEnd;
                  const stripLeft = isEffectiveEnd && start && start <= effectiveEnd;

                  // Pulse on the currently-active selection target
                  const isActiveTarget =
                    (selMode === 'start' && isStart) ||
                    (selMode === 'end' && isEnd);

                  let cls = 'crp__day';
                  if (isStart) cls += ' crp__day--start';
                  if (isEnd || isEffectiveEnd) cls += ' crp__day--end';
                  if (inRange) cls += ' crp__day--range';
                  if (isToday && !isStart && !isEnd && !isEffectiveEnd) cls += ' crp__day--today';
                  if (stripRight) cls += ' crp__day--strip-right';
                  if (stripLeft) cls += ' crp__day--strip-left';
                  if (isActiveTarget) cls += ' crp__day--active-target';

                  return (
                    <div
                      key={i}
                      className={cls}
                      onClick={() => handleDayClick(date)}
                      onMouseEnter={() => selMode === 'end' && start && setHoverDate(date)}
                      onMouseLeave={() => setHoverDate(null)}
                    >
                      <span>{date.getDate()}</span>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* ── Month picker ── */}
          {viewMode === 'months' && (
            <div className="crp__month-picker">
              {MONTHS_SHORT.map((m, i) => (
                <button
                  key={m}
                  type="button"
                  className={`crp__month-btn${i === viewMonth ? ' crp__month-btn--active' : ''}`}
                  onClick={() => selectMonth(i)}
                >
                  {m}
                </button>
              ))}
            </div>
          )}

          {/* ── Year picker ── */}
          {viewMode === 'years' && (
            <div className="crp__year-picker">
              {years.map(y => (
                <button
                  key={y}
                  type="button"
                  className={`crp__year-btn${y === viewYear ? ' crp__year-btn--active' : ''}`}
                  onClick={() => selectYear(y)}
                >
                  {y}
                </button>
              ))}
            </div>
          )}

          {/* Footer */}
          <div className="crp__footer">
            {selMode === 'start' || !startDate
              ? <span className="crp__hint">Startdatum wählen</span>
              : <span className="crp__hint">Enddatum wählen…</span>
            }
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarRangePicker;
